import React, { useMemo } from 'react';
import { FormDrawer } from './FormDrawer';
import { SearchableSelect } from './SearchableSelect';
import SaleLineSelectionTable from '../commercialRequests/SaleLineSelectionTable';
import {
  REQUEST_STATUS,
  getActiveRequestOrigins,
  getActiveRequestStatuses,
} from '../placeholders/planningDomain';
import {
  FORMULA_KIND_BASE,
  FORMULA_KIND_COLOR,
  PAINT_PRODUCT_OPTIONS,
} from '../placeholders/productionDomain';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

const emptyItem = () => ({
  product_code: '',
  product_name: '',
  color_code: '',
  color_name: '',
  formula_kind: FORMULA_KIND_COLOR,
  quantity: 1,
  unit: 'L',
});

export const emptyTcoatProductRequestForm = () => ({
  request_code: '',
  origin: 'Comercial',
  reference: '',
  needed_by_date: '',
  needed_by_time: '',
  status: REQUEST_STATUS.requested,
  quotation_code: '',
  sale_id: '',
  sale_code: '',
  notes: '',
  items: [emptyItem()],
});

const toDatetimeLocal = (dateValue, timeValue) => {
  const date = String(dateValue || '').slice(0, 10);
  if (!date) return '';
  const time = String(timeValue || '').slice(0, 5) || '00:00';
  return `${date}T${time}`;
};

const fromDatetimeLocal = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { date: '', time: '' };
  if (!raw.includes('T')) return { date: raw.slice(0, 10), time: '' };
  const [datePart, timePart = ''] = raw.split('T');
  return { date: String(datePart || '').slice(0, 10), time: String(timePart || '').slice(0, 5) };
};

export const TcoatProductRequestForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  saleOptions = [],
  saleLineItems = [],
  selectionMap = {},
  onSelectionUpdate,
  saleLinesLoading = false,
  onSaleChange,
}) => {
  const catalogTick = useCommercialCatalogRefresh();
  const originOptions = useMemo(
    () => getActiveRequestOrigins().map((value) => ({ value, label: value })),
    [catalogTick]
  );
  const statusOptions = useMemo(
    () => getActiveRequestStatuses().map((value) => ({ value, label: value })),
    [catalogTick]
  );
  const items = Array.isArray(formData?.items) ? formData.items : [];
  const useSaleLines = !isEditing;

  const updateItem = (index, key, value) => {
    const next = items.map((item, idx) => {
      if (idx !== index) return item;
      const patch = { ...item, [key]: value };
      if (key === 'product_code') {
        const option = PAINT_PRODUCT_OPTIONS.find((row) => row.value === value);
        if (option) {
          patch.product_name = option.label.split('·').slice(1).join('·').trim() || option.label;
        }
      }
      return patch;
    });
    onChange('items', next);
  };

  const addItem = () => onChange('items', [...items, emptyItem()]);
  const removeItem = (index) => onChange('items', items.filter((_, idx) => idx !== index));

  const handleDeadlineChange = (value) => {
    const { date, time } = fromDatetimeLocal(value);
    onChange('needed_by_deadline', { date, time });
  };

  const createFields = [
    {
      key: 'reference',
      label: 'Solicitante',
      disabled: true,
      readOnly: true,
    },
    {
      key: 'sale_id',
      label: 'Venta',
      type: 'custom',
      fullWidth: true,
      render: ({ baseInputStyle }) => (
        <SearchableSelect
          value={formData.sale_id || ''}
          options={saleOptions}
          onChange={(value) => {
            onChange('sale_id', value);
            onSaleChange?.(value);
          }}
          placeholder="Escribe para filtrar ventas…"
          disabled={isSaving}
          style={baseInputStyle}
          isDarkMode={isDarkMode}
          maxResults={120}
        />
      ),
    },
    {
      key: 'needed_by_datetime',
      label: 'Fecha y hora límite',
      type: 'custom',
      fullWidth: true,
      render: ({ baseInputStyle }) => (
        <input
          type="datetime-local"
          style={baseInputStyle}
          value={toDatetimeLocal(formData.needed_by_date, formData.needed_by_time)}
          onChange={(e) => handleDeadlineChange(e.target.value)}
          disabled={isSaving}
        />
      ),
    },
    { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
  ];

  const editFields = [
    { key: 'request_code', label: 'Código', disabled: true, readOnly: true },
    {
      key: 'origin',
      label: 'Origen',
      type: 'select',
      options: originOptions,
    },
    { key: 'reference', label: 'Solicitante' },
    {
      key: 'needed_by_datetime',
      label: 'Fecha y hora límite',
      type: 'custom',
      fullWidth: true,
      render: ({ baseInputStyle }) => (
        <input
          type="datetime-local"
          style={baseInputStyle}
          value={toDatetimeLocal(formData.needed_by_date, formData.needed_by_time)}
          onChange={(e) => handleDeadlineChange(e.target.value)}
          disabled={isSaving}
        />
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: statusOptions,
    },
    { key: 'quotation_code', label: 'Cotización' },
    { key: 'sale_code', label: 'Venta' },
    { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
  ];

  const sections = [
    {
      key: 'basic',
      title: 'Datos de la solicitud',
      fields: useSaleLines ? createFields : editFields,
    },
    {
      key: 'items',
      title: useSaleLines ? 'Ítems T-COAT de la venta' : 'Productos T-COAT',
      fields: [
        {
          key: 'items',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) =>
            useSaleLines ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {!formData.sale_id ? (
                  <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>
                    Selecciona una venta para ver los ítems cotizados y marcar qué solicitar.
                  </p>
                ) : saleLinesLoading ? (
                  <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Cargando ítems de la venta…</p>
                ) : (
                  <SaleLineSelectionTable
                    items={saleLineItems}
                    selectionMap={selectionMap}
                    onUpdate={onSelectionUpdate}
                    emptyLabel="Esta venta no tiene ítems T-COAT."
                    disabled={isSaving}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={addItem}
                    style={{
                      height: 30,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Agregar producto
                  </button>
                </div>
                {items.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Agrega al menos un producto.</p>
                ) : (
                  items.map((item, index) => (
                    <div
                      key={`item-${index}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 1fr 0.8fr 0.8fr 0.6fr 0.5fr auto',
                        gap: 8,
                        padding: 10,
                        borderRadius: 8,
                        border: `1px solid ${colors.sectionBorder}`,
                        background: colors.sectionBg,
                        alignItems: 'end',
                      }}
                    >
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Producto
                        <select
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.product_code || ''}
                          onChange={(e) => updateItem(index, 'product_code', e.target.value)}
                        >
                          <option value="">Seleccionar…</option>
                          {PAINT_PRODUCT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Nombre
                        <input
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.product_name || ''}
                          onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Cód. color
                        <input
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.color_code || ''}
                          onChange={(e) => updateItem(index, 'color_code', e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Color
                        <input
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.color_name || ''}
                          onChange={(e) => updateItem(index, 'color_name', e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Cant.
                        <input
                          type="number"
                          step="0.01"
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.quantity ?? 1}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Unidad
                        <input
                          style={{ ...baseInputStyle, marginTop: 4 }}
                          value={item.unit || 'L'}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                        />
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <select
                          style={{ ...baseInputStyle, fontSize: 11 }}
                          value={item.formula_kind || FORMULA_KIND_COLOR}
                          onChange={(e) => updateItem(index, 'formula_kind', e.target.value)}
                        >
                          <option value={FORMULA_KIND_COLOR}>Color</option>
                          <option value={FORMULA_KIND_BASE}>Base</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          style={{
                            height: 34,
                            padding: '0 10px',
                            borderRadius: 8,
                            border: `1px solid ${colors.inputBorder}`,
                            background: 'transparent',
                            color: '#b91c1c',
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ),
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar solicitud T-COAT' : 'Nueva solicitud T-COAT'}
      subtitle={
        isEditing
          ? 'Comercial · Producción'
          : 'Anexa una venta y selecciona los ítems a solicitar'
      }
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={820}
    />
  );
};

export default TcoatProductRequestForm;
